import { useState, useEffect } from 'react';
import axios from 'axios';
import { Briefcase, Plus, Trash2, Loader2, X, Upload, Sparkles, Check, Zap } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ordinal = (n) => {
  const v = n % 100;
  if (v >= 11 && v <= 13) return `${n}th`;
  const suffix = ['th', 'st', 'nd', 'rd'][n % 10] || 'th';
  return `${n}${suffix}`;
};

export default function RycomMeetings({ month, monthLabel, employees, daysInMonth, meetings: meetingsProp, setMeetings: setMeetingsProp }) {
  const [localMeetings, setLocalMeetings] = useState([]);
  const meetings = meetingsProp ?? localMeetings;
  const setMeetings = setMeetingsProp ?? setLocalMeetings;
  const [loading, setLoading] = useState(meetingsProp === undefined);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ title: '', day: '', time: '', employee_id: '', custom_name: '', notes: '', auto_assign: true });
  const [shiftsForMonth, setShiftsForMonth] = useState([]);

  // Image OCR state
  const [showUpload, setShowUpload] = useState(false);
  const [imageBase64, setImageBase64] = useState('');
  const [imageDataUrl, setImageDataUrl] = useState('');
  const [parsing, setParsing] = useState(false);
  const [parsedMeetings, setParsedMeetings] = useState([]);
  const [savingBulk, setSavingBulk] = useState(false);

  useEffect(() => {
    if (!month) { setLoading(false); return; }
    // Always fetch shifts (needed for auto-assign preview)
    axios.get(`${API}/api/team/shifts?month=${month}`, { withCredentials: true })
      .then(res => setShiftsForMonth(res.data || []))
      .catch(() => {});
    if (meetingsProp !== undefined) { setLoading(false); return; }
    setLoading(true);
    axios.get(`${API}/api/team/rycom-meetings`, { params: { month }, withCredentials: true })
      .then(res => setMeetings(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  // Compute the employee working the given day (earliest start = primary attendee)
  const attendeeForDay = (day) => {
    if (!day) return null;
    const dayShifts = shiftsForMonth.filter(s => s.day === parseInt(day, 10));
    if (dayShifts.length === 0) return null;
    dayShifts.sort((a, b) => (a.start || '99:99').localeCompare(b.start || '99:99'));
    const first = dayShifts[0];
    return employees.find(e => e.id === first.employee_id) || null;
  };
  const autoEmp = form.auto_assign ? attendeeForDay(form.day) : null;

  const empName = (id) => employees.find(e => e.id === id)?.name || '';
  const empColor = (id) => employees.find(e => e.id === id)?.color || '#facc15';
  // Returns name + color for a meeting (employee from list OR custom typed name)
  const attendeeFor = (m) => {
    if (m.employee_id) {
      const emp = employees.find(e => e.id === m.employee_id);
      if (emp) return { name: emp.name, color: emp.color };
    }
    return { name: m.attendee_name || '—', color: '#facc15' };
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.day) return;
    let empId = form.employee_id;
    const customName = form.custom_name.trim();
    if (form.auto_assign && autoEmp && !empId && !customName) {
      empId = autoEmp.id;
    }
    if (!empId && !customName) return;
    setAdding(true);
    try {
      const { data } = await axios.post(`${API}/api/team/rycom-meetings`, {
        month,
        day: parseInt(form.day, 10),
        title: form.title.trim(),
        employee_id: empId || '',
        attendee_name: customName,
        time: form.time.trim(),
        notes: form.notes.trim(),
      }, { withCredentials: true });
      setMeetings(list => [...list, data].sort((a, b) => a.day - b.day));
      setForm({ title: '', day: '', time: '', employee_id: '', custom_name: '', notes: '', auto_assign: form.auto_assign });
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to add meeting');
    }
    setAdding(false);
  };

  const onFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      setImageDataUrl(dataUrl);
      setImageBase64(String(dataUrl).split(',')[1] || '');
      setParsedMeetings([]);
    };
    reader.readAsDataURL(file);
  };

  const runParse = async () => {
    if (!imageBase64) return;
    setParsing(true);
    setParsedMeetings([]);
    try {
      const { data } = await axios.post(`${API}/api/team/rycom-meetings/parse`,
        { month, image_base64: imageBase64 },
        { withCredentials: true });
      // Ensure every parsed row has a month (fallback to current view month)
      const rows = (data.meetings || []).map(m => ({ ...m, month: m.month || month }));
      setParsedMeetings(rows);
      if (rows.length === 0) alert('No meetings detected — try a clearer photo.');
    } catch (err) {
      alert(err.response?.data?.detail || 'Parse failed');
    }
    setParsing(false);
  };

  const updateParsedRow = (idx, patch) => {
    setParsedMeetings(list => list.map((m, i) => i === idx ? { ...m, ...patch } : m));
  };

  const removeParsedRow = (idx) => {
    setParsedMeetings(list => list.filter((_, i) => i !== idx));
  };

  const saveAllParsed = async () => {
    if (parsedMeetings.length === 0) return;
    setSavingBulk(true);
    try {
      const { data } = await axios.post(`${API}/api/team/rycom-meetings/bulk`,
        { month, meetings: parsedMeetings },
        { withCredentials: true });
      // Only append rows that belong to the currently-viewed month
      const forCurrent = (data.created || []).filter(c => c.month === month);
      setMeetings(list => [...list, ...forCurrent].sort((a, b) => a.day - b.day));
      // Friendly summary if meetings landed in multiple months
      const byMonth = data.by_month || {};
      const monthKeys = Object.keys(byMonth);
      if (monthKeys.length > 1) {
        const summary = monthKeys.sort().map(mm => `${mm}: ${byMonth[mm]}`).join('  ·  ');
        alert(`Saved ${data.count} meetings across ${monthKeys.length} months:\n${summary}\n\nOnly ${forCurrent.length} shown here — the rest will appear when you navigate to their month.`);
      } else if (data.count > 0 && forCurrent.length === 0) {
        alert(`Saved ${data.count} meetings to ${monthKeys[0]}. They'll appear when you navigate there.`);
      }
      setParsedMeetings([]);
      setImageBase64('');
      setImageDataUrl('');
      setShowUpload(false);
    } catch (err) {
      alert(err.response?.data?.detail || 'Bulk save failed');
    }
    setSavingBulk(false);
  };

  const [confirmingId, setConfirmingId] = useState(null);
  const remove = async (id) => {
    try {
      await axios.delete(`${API}/api/team/rycom-meetings/${id}`, { withCredentials: true });
      setMeetings(list => list.filter(m => m.id !== id));
      setConfirmingId(null);
    } catch (err) {
      alert('Delete failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  return (
    <div className="border-2 border-amber-700/30 bg-amber-900/5 rounded-2xl p-4 sm:p-5" data-testid="rycom-meetings-panel">
      <div className="flex items-center gap-2 mb-3">
        <Briefcase size={16} className="text-amber-500" />
        <h3 className="font-heading text-sm tracking-wide uppercase text-white">Rycom Meetings — {monthLabel}</h3>
        <span className="font-sans text-[10px] text-neutral-500 ml-auto">{meetings.length} scheduled</span>
        <button
          type="button"
          onClick={() => setShowUpload(v => !v)}
          data-testid="rycom-upload-toggle"
          className="flex items-center gap-1 font-heading text-[10px] tracking-widest uppercase bg-amber-700/30 border border-amber-700/50 text-amber-300 hover:bg-amber-700/50 px-2.5 py-1 rounded-lg transition-colors"
        >
          {showUpload ? <X size={11} /> : <Upload size={11} />}
          {showUpload ? 'Manual Entry' : 'Upload Image'}
        </button>
      </div>

      {/* IMAGE UPLOAD MODE */}
      {showUpload && (
        <div className="mb-4 space-y-3 border border-amber-700/40 bg-black/40 rounded-xl p-4" data-testid="rycom-upload-panel">
          <p className="font-sans text-xs text-neutral-400">
            <Sparkles size={11} className="inline text-amber-500 mr-1" />
            Take a photo of the monthly Rycom management meeting list. AI will read each row and auto-assign the person working that day's shift.
          </p>

          {!imageDataUrl ? (
            <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-amber-700/40 rounded-xl p-8 cursor-pointer hover:bg-amber-500/5 transition-colors" data-testid="rycom-file-drop">
              <Upload size={24} className="text-amber-500" />
              <span className="font-heading text-xs tracking-widest uppercase text-amber-300">Choose Image</span>
              <span className="font-sans text-[10px] text-neutral-500">PNG / JPG</span>
              <input type="file" accept="image/*" className="hidden" onChange={e => onFile(e.target.files?.[0])} data-testid="rycom-file-input" />
            </label>
          ) : (
            <div className="space-y-3">
              <div className="flex gap-3">
                <img src={imageDataUrl} alt="Uploaded" className="max-h-48 rounded-lg border border-neutral-700" />
                <div className="flex flex-col gap-2">
                  <button
                    onClick={runParse}
                    disabled={parsing}
                    data-testid="rycom-parse-btn"
                    className="flex items-center gap-2 font-heading text-xs tracking-widest uppercase bg-amber-500 text-black px-4 py-2 rounded-lg hover:bg-amber-400 transition-colors disabled:opacity-50"
                  >
                    {parsing ? <Loader2 size={13} className="animate-spin" /> : <Zap size={13} />}
                    {parsing ? 'Reading...' : 'Read Image'}
                  </button>
                  <button
                    onClick={() => { setImageBase64(''); setImageDataUrl(''); setParsedMeetings([]); }}
                    className="font-heading text-[10px] tracking-widest uppercase text-neutral-500 hover:text-white transition-colors"
                  >
                    Replace
                  </button>
                </div>
              </div>

              {parsedMeetings.length > 0 && (
                <div className="border border-neutral-700 rounded-lg overflow-hidden" data-testid="rycom-parsed-list">
                  <div className="bg-amber-900/20 px-3 py-2 flex items-center justify-between flex-wrap gap-2">
                    <span className="font-heading text-[11px] tracking-widest uppercase text-amber-300">
                      <Check size={11} className="inline mr-1" /> Found {parsedMeetings.length} meeting{parsedMeetings.length !== 1 ? 's' : ''}
                      {(() => {
                        const monthCounts = parsedMeetings.reduce((acc, m) => {
                          acc[m.month] = (acc[m.month] || 0) + 1;
                          return acc;
                        }, {});
                        const months = Object.keys(monthCounts).sort();
                        if (months.length <= 1) return null;
                        return (
                          <span className="ml-2 font-sans normal-case text-[10px] text-amber-200/70">
                            across {months.length} months ({months.map(mm => `${mm}: ${monthCounts[mm]}`).join(' · ')})
                          </span>
                        );
                      })()}
                    </span>
                    <button
                      onClick={saveAllParsed}
                      disabled={savingBulk}
                      data-testid="rycom-save-all-btn"
                      className="flex items-center gap-1 font-heading text-[10px] tracking-widest uppercase bg-amber-500 text-black px-3 py-1 rounded hover:bg-amber-400 transition-colors disabled:opacity-50"
                    >
                      {savingBulk ? <Loader2 size={11} className="animate-spin" /> : <Plus size={11} />}
                      Save All
                    </button>
                  </div>
                  <div className="max-h-72 overflow-y-auto">
                    {parsedMeetings.map((m, idx) => (
                      <div key={idx} className="flex items-center gap-2 px-3 py-2 border-b border-neutral-800/50 last:border-0 text-xs" data-testid={`rycom-parsed-row-${idx}`}>
                        <input
                          type="month"
                          value={m.month}
                          onChange={e => updateParsedRow(idx, { month: e.target.value })}
                          className={`w-32 bg-black border rounded px-2 py-1 font-mono text-center ${m.month !== month ? 'border-amber-500/70 text-amber-300' : 'border-neutral-700 text-neutral-400'}`}
                          title={m.month !== month ? 'This meeting will be saved to a different month' : 'Same as current view'}
                        />
                        <input
                          type="number"
                          min="1"
                          max="31"
                          value={m.day}
                          onChange={e => updateParsedRow(idx, { day: parseInt(e.target.value, 10) || 0 })}
                          className="w-12 bg-black border border-neutral-700 rounded px-2 py-1 text-center font-mono"
                        />
                        <input
                          value={m.time}
                          onChange={e => updateParsedRow(idx, { time: e.target.value })}
                          placeholder="—"
                          className="w-16 bg-black border border-neutral-700 rounded px-2 py-1 font-mono text-center"
                        />
                        <input
                          value={m.title}
                          onChange={e => updateParsedRow(idx, { title: e.target.value })}
                          className="flex-1 bg-black border border-neutral-700 rounded px-2 py-1"
                        />
                        <select
                          value={m.employee_id || ''}
                          onChange={e => updateParsedRow(idx, { employee_id: e.target.value, attendee_name: '' })}
                          className="bg-black border border-neutral-700 rounded px-2 py-1"
                        >
                          <option value="">— No auto —</option>
                          {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
                        </select>
                        <button
                          onClick={() => removeParsedRow(idx)}
                          className="text-neutral-500 hover:text-brand p-1 transition-colors"
                          title="Remove"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Add form (hidden when upload panel is open) */}
      {!showUpload && (
      <form onSubmit={submit} className="space-y-2 mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-12 gap-2">
          <input
            value={form.title}
            onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            placeholder="Meeting title (e.g. Rycom Manager Meeting)"
            data-testid="meeting-title-input"
            className="sm:col-span-7 bg-black border border-neutral-700 rounded-lg px-3 py-2 font-sans text-sm text-white focus:border-amber-500 focus:outline-none placeholder-neutral-600"
          />
          <select
            value={form.day}
            onChange={e => setForm(f => ({ ...f, day: e.target.value }))}
            data-testid="meeting-day-select"
            className="sm:col-span-3 bg-black border border-neutral-700 rounded-lg px-3 py-2 font-sans text-sm text-white focus:border-amber-500 focus:outline-none"
          >
            <option value="">Day</option>
            {Array.from({ length: daysInMonth || 31 }, (_, i) => i + 1).map(d => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
          <input
            value={form.time}
            onChange={e => setForm(f => ({ ...f, time: e.target.value }))}
            placeholder="Time"
            data-testid="meeting-time-input"
            className="sm:col-span-2 bg-black border border-neutral-700 rounded-lg px-3 py-2 font-sans text-sm text-white focus:border-amber-500 focus:outline-none placeholder-neutral-600"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-stretch">
          <select
            value={form.employee_id}
            onChange={e => setForm(f => ({ ...f, employee_id: e.target.value, custom_name: e.target.value ? '' : f.custom_name }))}
            data-testid="meeting-employee-select"
            className="sm:col-span-5 bg-black border border-neutral-700 rounded-lg px-3 py-2 font-sans text-sm text-white focus:border-amber-500 focus:outline-none"
          >
            <option value="">Assign to employee...</option>
            {employees.map(emp => (
              <option key={emp.id} value={emp.id}>{emp.name}</option>
            ))}
          </select>
          <div className="sm:col-span-1 flex items-center justify-center">
            <span className="font-heading text-[10px] tracking-widest text-neutral-500">OR</span>
          </div>
          <input
            value={form.custom_name}
            onChange={e => setForm(f => ({ ...f, custom_name: e.target.value, employee_id: e.target.value ? '' : f.employee_id }))}
            placeholder="Type a custom name (e.g. Mall Manager)"
            data-testid="meeting-custom-name-input"
            className="sm:col-span-5 bg-black border border-neutral-700 rounded-lg px-3 py-2 font-sans text-sm text-white focus:border-amber-500 focus:outline-none placeholder-neutral-600"
          />
          <button
            type="submit"
            disabled={adding || !form.title.trim() || !form.day || (!form.employee_id && !form.custom_name.trim() && !(form.auto_assign && autoEmp))}
            data-testid="meeting-add-btn"
            className="sm:col-span-1 flex items-center justify-center font-heading text-xs tracking-widest uppercase bg-amber-600 text-white px-3 py-2 rounded-lg hover:bg-amber-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {adding ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          </button>
        </div>

        {/* Auto-assign toggle + live badge */}
        <label className="flex items-center gap-2 cursor-pointer group pt-1">
          <input
            type="checkbox"
            checked={form.auto_assign}
            onChange={e => setForm(f => ({ ...f, auto_assign: e.target.checked }))}
            data-testid="meeting-auto-assign-toggle"
            className="peer appearance-none w-4 h-4 border border-neutral-700 rounded checked:bg-amber-500 checked:border-amber-500 transition-colors relative after:content-['✓'] after:absolute after:inset-0 after:flex after:items-center after:justify-center after:text-black after:text-[10px] after:opacity-0 checked:after:opacity-100"
          />
          <Sparkles size={11} className="text-amber-500" />
          <span className="font-sans text-xs text-neutral-300">
            Auto-assign the person on shift that day
          </span>
          {form.auto_assign && form.day && (
            autoEmp ? (
              <span
                className="ml-2 flex items-center gap-1.5 font-heading text-[10px] tracking-widest uppercase px-2 py-0.5 rounded-full"
                style={{ background: `${autoEmp.color}22`, border: `1px solid ${autoEmp.color}80`, color: autoEmp.color }}
                data-testid="meeting-auto-assign-preview"
              >
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: autoEmp.color, boxShadow: `0 0 6px ${autoEmp.color}` }} />
                → {autoEmp.name}
              </span>
            ) : (
              <span className="ml-2 font-sans text-[10px] italic text-neutral-500">No shift scheduled that day</span>
            )
          )}
        </label>
      </form>
      )}

      {/* List */}
      {loading ? (
        <p className="font-sans text-xs text-neutral-500 italic">Loading...</p>
      ) : meetings.length === 0 ? (
        <p className="font-sans text-xs text-neutral-500 italic">No meetings scheduled this month. Add one above.</p>
      ) : (
        <div className="space-y-2">
          {meetings.map(m => {
            const att = attendeeFor(m);
            return (
            <div key={m.id} data-testid={`meeting-row-${m.id}`} className="flex items-center gap-3 bg-black/50 border border-neutral-800 rounded-lg px-3 py-2 hover:border-amber-700/50 transition-colors">
              <div className="shrink-0 w-16 text-center">
                <div
                  className="font-heading text-2xl leading-none tracking-tight"
                  style={{ color: '#facc15', textShadow: '0 0 8px rgba(250, 204, 21, 0.7), 0 0 16px rgba(250, 204, 21, 0.4)' }}
                >
                  {ordinal(m.day)}
                </div>
                {m.time && (
                  <div className="text-[10px] font-mono mt-1" style={{ color: '#fde047', textShadow: '0 0 4px rgba(250, 204, 21, 0.5)' }}>
                    {m.time}
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-sans text-sm text-white truncate">{m.title}</p>
                {m.notes && <p className="font-sans text-[10px] text-neutral-500 truncate">{m.notes}</p>}
              </div>
              <div
                className="flex items-center gap-2 shrink-0 px-3 py-1.5 rounded-lg"
                style={{
                  background: `${att.color}22`,
                  border: `1.5px solid ${att.color}`,
                  boxShadow: `0 0 12px ${att.color}55`,
                }}
              >
                <span
                  className="w-2.5 h-2.5 rounded-full shrink-0"
                  style={{ background: att.color, boxShadow: `0 0 8px ${att.color}` }}
                />
                <span
                  className="font-heading text-base sm:text-lg font-bold tracking-wide uppercase whitespace-nowrap"
                  style={{ color: att.color, textShadow: `0 0 6px ${att.color}88` }}
                >
                  {att.name}
                </span>
              </div>
              {confirmingId === m.id ? (
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => remove(m.id)}
                    data-testid={`meeting-delete-confirm-${m.id}`}
                    className="font-heading text-[10px] tracking-widest uppercase bg-brand text-white px-2 py-1 rounded hover:bg-brand-hover transition-colors"
                  >
                    Delete?
                  </button>
                  <button
                    onClick={() => setConfirmingId(null)}
                    className="font-heading text-[10px] tracking-widest uppercase text-neutral-400 px-2 py-1 hover:text-white transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button onClick={() => setConfirmingId(m.id)} data-testid={`meeting-delete-${m.id}`} className="text-neutral-500 hover:text-brand p-1.5 transition-colors shrink-0">
                  <Trash2 size={14} />
                </button>
              )}
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
